import { DeviceProblem } from "src/app/generated/CrossPoint.RemoteXpac.Server";
import { shortMac } from "./common.functions";
import { TranslateService } from "@ngx-translate/core";

const NOTIFICATION_BOX_MISSING_PROBLEM_NUMBER: number = 8196;
const ANTENNA_DISCONNECTED_PROBLEM_NUMBER: number = 9216;
const IR_COUNTER_BLOCKED_PROBLEM_NUMBER: number = 12288;
const OTHER_XPAC_DETECTED_PROBLEM_NUMBER: number = 4107;
const REPORT_UPLOAD_FAILED_PROBLEM_NUMBER: number = 4103;

export function getProblemTranslation(problem: DeviceProblem, translate: TranslateService): string {
  switch (problem.problemCode) {
    case NOTIFICATION_BOX_MISSING_PROBLEM_NUMBER: {
      return getNotificationBoxMissingProblemTranslation(problem, translate);
    }
    case ANTENNA_DISCONNECTED_PROBLEM_NUMBER: {
      return getRfidAntennaDisconnectedProblemTranslation(problem, translate);
    }
    case IR_COUNTER_BLOCKED_PROBLEM_NUMBER: {
      return getCounterBlockedProblemTranslation(problem, translate);
    }
    default: {
      const key: string = `Modules.RemoteMaintenance.Problems.${problem.problemCode}.problem`;
      const translation: string = translate.instant(key);
      if (translation !== key) {
        return translation;
      }
      return translate.instant('Modules.RemoteMaintenance.Problems.Unknown', { problemCode: problem.problemCode });
    }
  }
}

export function getProblemInfoTranslation(problem: DeviceProblem): string {
  switch (problem.problemCode) {
    case OTHER_XPAC_DETECTED_PROBLEM_NUMBER: {
      // Data is the MAC address of the other XPAC
      let mac: string = '';
      problem.data.forEach((byte: number) => {
        mac += byte.toString(16).padStart(2, '0').toUpperCase();
      });
      return mac;
    }
    case REPORT_UPLOAD_FAILED_PROBLEM_NUMBER: {
      // Data is a UTF-8 string containing the name of the report
      const decoder: TextDecoder = new TextDecoder('utf-8');
      return decoder.decode(new Uint8Array(problem.data));
    }
    default: {
      return shortMac(problem.mac);
    }
  }
}

export function getNotificationBoxMissingProblemTranslation(problem: DeviceProblem, translate: TranslateService): string {
  const typedProblem: NotificationBoxMissingProblem = deserializeNotificationBoxMissingProblem(problem);
  return translate.instant(`Modules.RemoteMaintenance.Problems.${problem.problemCode}.problem`, typedProblem);
}

export function getRfidAntennaDisconnectedProblemTranslation(problem: DeviceProblem, translate: TranslateService): string {
  const typedProblem = deserializeAntennaDisconnectedProblem(problem);
  return translate.instant(`Modules.RemoteMaintenance.Problems.${problem.problemCode}.problem`, typedProblem);
}

export function getCounterBlockedProblemTranslation(problem: DeviceProblem, translate: TranslateService): string {
  const typedProblem: IrCounterBlockedProblem = deserializeIrCounterBlockedProblem(problem);

  const sideTranslation: string = translate.instant(`Modules.RemoteMaintenance.Problems.${problem.problemCode}.side.${typedProblem.side}`);
  const boardTranslation: string = (typedProblem.board + 1).toString();

  return translate.instant(`Modules.RemoteMaintenance.Problems.${problem.problemCode}.problem`, {
    side: sideTranslation,
    board: boardTranslation
  });
}

// Note: these are modified versions of the generated code in DEVEX (generated by the ProblemGenerator, not the XPAC API)

interface IrCounterBlockedProblem extends DeviceProblem {
  /**
   * Which side of the antenna was blocked, zero indexed
   */
  readonly side: number;
  /**
   * Which of the boards was blocked, zero indexed
   */
  readonly board: number;
}

function deserializeIrCounterBlockedProblem(problem: DeviceProblem): IrCounterBlockedProblem {
  return {
    ...problem,
    side: problem.data[0],
    board: problem.data[1],
  };
}

interface NotificationBoxMissingProblem extends DeviceProblem {
  /**
   * Which box is missing, counting starts at 1
   */
  readonly box: number;
}

function deserializeNotificationBoxMissingProblem(problem: DeviceProblem): NotificationBoxMissingProblem {
  return {
    ...problem,
    box: problem.data[0],
  };
}

interface AntennaDisconnectedProblem extends DeviceProblem {
  /**
   * The antenna number that is disconnected
   */
  readonly antenna: number;
}

function deserializeAntennaDisconnectedProblem(problem: DeviceProblem): AntennaDisconnectedProblem {
  return {
    ...problem,
    antenna: problem.data[0],
  };
}
